import { useEffect, useState } from "react";

/**
 * useResolvedAssetVersionContent — EMAILS-IMPL-026
 *
 * Resolves the ACTUAL canonical content for one exact governed
 * `{assetId, version}` pin, via the SAME already-existing per-asset
 * Version-history API every Emails panel already uses (`slot.versionsFn`,
 * compositionSlots.js) -- no new backend endpoint. Shared by
 * EmailBuilderCanvasBlock.jsx (Hook/Email Body/Offer/CTA/P.S./Image) and
 * EmailBuilderMetadata.jsx (Subject/Preview/Landing Page) so both surfaces
 * resolve exact-Version content identically, never two subtly different
 * fetch/match implementations.
 *
 * EXACT-VERSION INTEGRITY (checkpoint invariant, unchanged): matches
 * strictly on `version === exact pinned version`. Never falls back to the
 * Asset's own current_version, never substitutes the most recent entry,
 * never guesses. A pin that does not resolve is reported as `"unresolved"`
 * -- the caller is expected to render an explicit diagnostic
 * ("Unable to resolve HOK0001 @ 2.0.0"), never a silent "—" (checkpoint's
 * own explicit "must not silently render '—' when exact governed content
 * should exist" requirement).
 *
 * Returns one of:
 *   { status: "idle" }                        -- no assetId/version pinned yet
 *   { status: "loading" }                      -- fetch in flight
 *   { status: "unresolved" }                   -- fetch failed, OR no Version
 *                                                  entry matched the exact
 *                                                  pinned version string
 *   { status: "resolved", content }            -- exact match found;
 *                                                  `content` is that
 *                                                  Version's own nested
 *                                                  `content` object exactly
 *                                                  as the backend returns it
 *                                                  (e.g. `{canonical_hook: "..."}`
 *                                                  for Hook) -- never
 *                                                  reshaped here.
 */
export function useResolvedAssetVersionContent(versionsFn, assetId, version) {
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!versionsFn || !assetId || !version) {
      setState({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading" });
    (async () => {
      try {
        const versions = await versionsFn(assetId);
        if (cancelled) return;
        const match = (versions || []).find((v) => v.version === version);
        if (!match) {
          setState({ status: "unresolved" });
          return;
        }
        setState({ status: "resolved", content: match.content || {} });
      } catch {
        if (!cancelled) setState({ status: "unresolved" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [versionsFn, assetId, version]);

  return state;
}
